import React, { useEffect, useState } from 'react';
import { Table, Input, Icon, Dropdown, Button } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import moment from 'moment';
import Heading from '../../components/Heading';
import Loader from '../../components/Loader';
import ErrorConsoleAI from '../../components/ErrorConsoleAI';
import CleanupModal from '../../components/CleanupModal';
import fetchCronJobs from '../../services/fetchCronJobs';
import fetchCronMonths from '../../services/fetchCronMonths';
import user from '../../store/user';
import './CronJobs.scss';

const CronJobs = () => {
    const [cronJobs, setCronJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [limit, setLimit] = useState(50);
    const [selectedJob, setSelectedJob] = useState(null);
    const [isConsoleOpen, setIsConsoleOpen] = useState(false);
    const [isCleanupModalOpen, setIsCleanupModalOpen] = useState(false);
    const [adminId, setAdminId] = useState(null);
    const [activeTab, setActiveTab] = useState('all'); // 'all', 'running', 'failed', 'success'

    // Date selectors
    const [selectedYear, setSelectedYear] = useState(moment().year());
    const [selectedMonth, setSelectedMonth] = useState(moment().month() + 1); // moment months are 0-indexed
    const [availableMonths, setAvailableMonths] = useState([]);

    const limitOptions = [
        { key: 10, text: '10', value: 10 },
        { key: 25, text: '25', value: 25 },
        { key: 50, text: '50', value: 50 },
        { key: 100, text: '100', value: 100 },
        { key: 'all', text: 'All', value: 999999 }
    ];

    // Fetch adminId once at mount so it's always available before any click handler runs
    useEffect(() => {
        user.getProfile().then(profile => setAdminId(profile.id)).catch(console.error);
    }, []);

    useEffect(() => {
        if (adminId) {
            loadCronJobs(adminId);
            loadAvailableMonths(adminId);
        }
    }, [adminId]);

    useEffect(() => {
        if (adminId) {
            loadCronJobs(adminId);
        }
    }, [limit, selectedYear, selectedMonth]);

    const loadCronJobs = async (id) => {
        const resolvedId = id || adminId;
        if (!resolvedId) return;
        try {
            setIsLoading(true);
            const response = await fetchCronJobs(resolvedId, {
                limit,
                year: selectedYear,
                month: selectedMonth
            });

            if (response.data && response.data.data && response.data.data.jobs) {
                setCronJobs(response.data.data.jobs);
                setFilteredJobs(response.data.data.jobs);
            }
        } catch (error) {
            console.error('Error fetching cron jobs:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (searchTerm === '') {
            setFilteredJobs(cronJobs);
        } else {
            const filtered = cronJobs.filter(job =>
                job.jobName.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredJobs(filtered);
        }
    }, [searchTerm, cronJobs]);

    const getStatusClass = (status) => {
        if (!status) return '';
        const s = status.toLowerCase();
        if (s === 'success') return 'status-success';
        if (s === 'running') return 'status-running';
        return 'status-failure';
    };

    const getDisplayedJobs = () => {
        if (activeTab === 'running') return filteredJobs.filter(j => j.state && j.state.currentStatus === 'running');
        if (activeTab === 'failed') return filteredJobs.filter(j => {
            const s = j.state && j.state.currentStatus;
            return s !== 'success' && s !== 'running';
        });
        if (activeTab === 'success') return filteredJobs.filter(j => j.state && j.state.currentStatus === 'success');
        return filteredJobs;
    };

    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    };

    const formatDuration = (ms) => {
        if (!ms) return 'N/A';
        if (ms < 1000) return `${ms}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
        return `${(ms / 60000).toFixed(2)}m`;
    };

    const formatDate = (date) => {
        if (!date) return 'Never';
        return moment(date).format('DD MMM YYYY, HH:mm:ss');
    };

    const formatRelativeTime = (date) => {
        if (!date) return '';
        return moment(date).fromNow();
    };

    const handleOpenErrorConsole = (job) => {
        setSelectedJob({ ...job, adminId });
        setIsConsoleOpen(true);
    };

    const handleCloseErrorConsole = () => {
        setIsConsoleOpen(false);
        setSelectedJob(null);
    };

    const loadAvailableMonths = async (id) => {
        const resolvedId = id || adminId;
        if (!resolvedId) return;
        try {
            const response = await fetchCronMonths(resolvedId, 'history');
            if (response.data && response.data.data && response.data.data.months) {
                setAvailableMonths(response.data.data.months);
            }
        } catch (error) {
            console.error('Error fetching available months:', error);
        }
    };

    const handleCleanupSuccess = () => {
        toast.success('Cleanup completed successfully!');
        loadAvailableMonths();
    };

    // Generate year options (current year and 2 years back)
    const yearOptions = [];
    const currentYear = moment().year();
    for (let i = 0; i <= 2; i++) {
        const year = currentYear - i;
        yearOptions.push({
            key: year,
            text: year.toString(),
            value: year
        });
    }

    // Generate month options
    const monthOptions = moment.months().map((month, index) => ({
        key: index + 1,
        text: month,
        value: index + 1
    }));

    return (
        <div className="cron-jobs-container">
            <div className="page-header">
                <Heading text="Cron Jobs Monitor" />
            </div>

            <div className="controls-section">
                <div className="search-box">
                    <Input
                        icon="search"
                        placeholder="Search by job name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>
                <div className="date-selectors">
                    <div className="selector-group">
                        <span className="selector-label">Year:</span>
                        <Dropdown
                            selection
                            options={yearOptions}
                            value={selectedYear}
                            onChange={(e, { value }) => setSelectedYear(value)}
                            className="year-dropdown"
                        />
                    </div>
                    <div className="selector-group">
                        <span className="selector-label">Month:</span>
                        <Dropdown
                            selection
                            options={monthOptions}
                            value={selectedMonth}
                            onChange={(e, { value }) => setSelectedMonth(value)}
                            className="month-dropdown"
                        />
                    </div>
                </div>
                <div className="limit-selector">
                    <span className="limit-label">Show:</span>
                    <Dropdown
                        selection
                        options={limitOptions}
                        value={limit}
                        onChange={(e, { value }) => setLimit(value)}
                        className="limit-dropdown"
                    />
                </div>
                <div className="action-buttons">
                    <Button
                        color="red"
                        icon
                        labelPosition="left"
                        onClick={() => setIsCleanupModalOpen(true)}
                        className="cleanup-btn"
                    >
                        <Icon name="trash alternate" />
                        Cleanup
                    </Button>
                    <Icon 
                        name="refresh" 
                        size="large" 
                        link 
                        onClick={() => loadCronJobs()}
                        title="Refresh"
                        className="refresh-icon"
                    />
                </div>
            </div>

            {isLoading ? (
                <div className="loader-wrapper">
                    <Loader />
                </div>
            ) : (
                <>
                    <div className="stats-summary">
                        <div className="stat-card" onClick={() => setActiveTab('all')} style={{ cursor: 'pointer' }}>
                            <div className="stat-value">{filteredJobs.length}</div>
                            <div className="stat-label">Total Jobs</div>
                        </div>
                        <div className="stat-card running" onClick={() => setActiveTab('running')} style={{ cursor: 'pointer' }}>
                            <div className="stat-value">
                                {filteredJobs.filter(j => j.state && j.state.currentStatus === 'running').length}
                            </div>
                            <div className="stat-label">Running</div>
                        </div>
                        <div className="stat-card success" onClick={() => setActiveTab('success')} style={{ cursor: 'pointer' }}>
                            <div className="stat-value">
                                {filteredJobs.filter(j => j.state && j.state.currentStatus === 'success').length}
                            </div>
                            <div className="stat-label">Successful</div>
                        </div>
                        <div className="stat-card failure" onClick={() => setActiveTab('failed')} style={{ cursor: 'pointer' }}>
                            <div className="stat-value">
                                {filteredJobs.filter(j => {
                                    const s = j.state && j.state.currentStatus;
                                    return s !== 'success' && s !== 'running';
                                }).length}
                            </div>
                            <div className="stat-label">Failed</div>
                        </div>
                    </div>

                    <div className="job-filter-tabs">
                        <button
                            className={`filter-tab ${activeTab === 'all' ? 'active' : ''}`}
                            onClick={() => setActiveTab('all')}
                        >
                            <Icon name="list" />
                            All <span className="tab-count">{filteredJobs.length}</span>
                        </button>
                        <button
                            className={`filter-tab running ${activeTab === 'running' ? 'active' : ''}`}
                            onClick={() => setActiveTab('running')}
                        >
                            <Icon loading name="spinner" />
                            Running <span className="tab-count">{filteredJobs.filter(j => j.state && j.state.currentStatus === 'running').length}</span>
                        </button>
                        <button
                            className={`filter-tab success ${activeTab === 'success' ? 'active' : ''}`}
                            onClick={() => setActiveTab('success')}
                        >
                            <Icon name="check circle" />
                            Success <span className="tab-count">{filteredJobs.filter(j => j.state && j.state.currentStatus === 'success').length}</span>
                        </button>
                        <button
                            className={`filter-tab failed ${activeTab === 'failed' ? 'active' : ''}`}
                            onClick={() => setActiveTab('failed')}
                        >
                            <Icon name="times circle" />
                            Failed <span className="tab-count">{filteredJobs.filter(j => { const s = j.state && j.state.currentStatus; return s !== 'success' && s !== 'running'; }).length}</span>
                        </button>
                    </div>

                    <div className="table-wrapper">
                        <Table celled striped className="cron-jobs-table">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Job Name</Table.HeaderCell>
                                    <Table.HeaderCell>Status</Table.HeaderCell>
                                    <Table.HeaderCell>Last Run</Table.HeaderCell>
                                    <Table.HeaderCell>Duration</Table.HeaderCell>
                                    <Table.HeaderCell>Total Runs</Table.HeaderCell>
                                    <Table.HeaderCell>Success Rate</Table.HeaderCell>
                                    <Table.HeaderCell>Consecutive Failures</Table.HeaderCell>
                                    <Table.HeaderCell>Last Failed</Table.HeaderCell>
                                    <Table.HeaderCell>Actions</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {filteredJobs.length === 0 ? (
                                    <Table.Row>
                                        <Table.Cell colSpan="9" textAlign="center">
                                            No cron jobs found
                                        </Table.Cell>
                                    </Table.Row>
                                ) : (
                                    getDisplayedJobs().map((job, index) => (
                                        <Table.Row
                                            key={index}
                                            className={getStatusClass(job.state && job.state.currentStatus)}
                                        >
                                            <Table.Cell className="job-name-cell">
                                                <div className="job-name">{job.jobName}</div>
                                            </Table.Cell>
                                            <Table.Cell className="status-cell">
                                                <div className={`status-badge ${getStatusClass(job.state && job.state.currentStatus)}`}>
                                                    <Icon
                                                        loading={job.state && job.state.currentStatus === 'running'}
                                                        name={
                                                            job.state && job.state.currentStatus === 'success' ? 'check circle' :
                                                            job.state && job.state.currentStatus === 'running' ? 'spinner' :
                                                            'times circle'
                                                        }
                                                    />
                                                    {job.state && job.state.currentStatus || 'Unknown'}
                                                </div>
                                                {job.state && job.state.currentStatus !== 'success' && job.state && job.state.currentStatus !== 'running' && job.state.error && (
                                                    <div className="error-message" title={job.state.error}>
                                                        {job.state.error}
                                                    </div>
                                                )}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="last-run-time">
                                                    {formatDate(job.state && job.state.lastRunAt)}
                                                </div>
                                                <div className="relative-time">
                                                    {formatRelativeTime(job.state && job.state.lastRunAt)}
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                {formatDuration(job.state && job.state.lastDurationMs)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {job.metrics && job.metrics.totalRuns || 0}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="success-rate">
                                                    {job.metrics && job.metrics.successRate ? `${job.metrics.successRate}%` : 'N/A'}
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className={`consecutive-failures ${job.metrics && job.metrics.consecutiveFailures > 0 ? 'has-failures' : ''}`}>
                                                    {job.metrics && job.metrics.consecutiveFailures || 0}
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell className="last-failed-cell">
                                                {job.state && job.state.lastFailureAt ? (
                                                    <>
                                                        <div className="last-run-time">{formatDate(job.state.lastFailureAt)}</div>
                                                        <div className="relative-time">{formatRelativeTime(job.state.lastFailureAt)}</div>
                                                    </>
                                                ) : (
                                                    <span className="never-failed">—</span>
                                                )}
                                            </Table.Cell>
                                            <Table.Cell className="actions-cell">
                                                <Button
                                                    size="small"
                                                    color={
                                                        job.state && job.state.currentStatus === 'success' ? 'blue' :
                                                        job.state && job.state.currentStatus === 'running' ? 'yellow' :
                                                        'red'
                                                    }
                                                    icon
                                                    labelPosition="left"
                                                    onClick={() => handleOpenErrorConsole(job)}
                                                    className="console-btn"
                                                >
                                                    <Icon
                                                        loading={job.state && job.state.currentStatus === 'running'}
                                                        name={
                                                            job.state && job.state.currentStatus === 'success' ? 'info circle' :
                                                            job.state && job.state.currentStatus === 'running' ? 'spinner' :
                                                            'bug'
                                                        }
                                                    />
                                                    {job.state && job.state.currentStatus === 'running' ? 'Details' :
                                                     job.state && job.state.currentStatus === 'success' ? 'Details' :
                                                     'Debug'}
                                                </Button>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))
                                )}
                            </Table.Body>
                        </Table>
                    </div>
                </>
            )}

            {/* Error Console AI */}
            {selectedJob && (
                <ErrorConsoleAI
                    isOpen={isConsoleOpen}
                    onClose={handleCloseErrorConsole}
                    jobData={selectedJob}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    adminId={adminId}
                />
            )}

            {/* Cleanup Modal */}
            <CleanupModal
                isOpen={isCleanupModalOpen}
                onClose={() => setIsCleanupModalOpen(false)}
                adminId={adminId}
                onSuccess={handleCleanupSuccess}
            />
        </div>
    );
};

export default CronJobs;
